<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SalesPartyDocumentNotice from './SalesPartyDocumentNotice.vue';
import SalesPartyDocumentsUploader from './SalesPartyDocumentsUploader.vue';
import { useSalesPartyDocuments } from '../composables/useSalesPartyDocuments';

const props = defineProps({
  transactionId: { type: [String, Number], required: true },
  partyId: { type: [String, Number], required: true },
  partyKind: { type: String, required: true },
  partyRole: { type: String, required: true },
  canEdit: { type: Boolean, default: false },
  documentTypes: { type: Array, default: () => [] },
  deliveryCorrection: { type: Boolean, default: false },
});

const emit = defineEmits(['changed']);
const { t } = useI18n();
const documents = useSalesPartyDocuments({
  transactionId: computed(() => props.transactionId),
  partyId: computed(() => props.partyId),
  t,
  notify: useAlert,
});
const savingDocumentId = ref(null);
const notices = ref({ required: true, legible: true, cpf: true });
const partyAction = computed(() =>
  props.partyRole === 'vendedor'
    ? t('SALES_PARTIES.DOCUMENTS.SELLS')
    : t('SALES_PARTIES.DOCUMENTS.BUYS')
);

const title = computed(() => {
  if (props.deliveryCorrection) {
    return t('SALES_PARTIES.DOCUMENTS.DELIVERY_TITLE', {
      action: partyAction.value,
    });
  }

  if (props.partyKind === 'pessoa_juridica') {
    return t('SALES_PARTIES.DOCUMENTS.COMPANY_TITLE', {
      action: partyAction.value,
    });
  }

  return t('SALES_PARTIES.DOCUMENTS.PERSON_TITLE', {
    action: partyAction.value,
  });
});
const description = computed(() =>
  props.deliveryCorrection
    ? t('SALES_PARTIES.DOCUMENTS.DELIVERY_DESCRIPTION')
    : t('SALES_PARTIES.DOCUMENTS.DESCRIPTION')
);
const requiredNoticeCopy = computed(() => {
  const company = props.partyKind === 'pessoa_juridica';
  return company
    ? {
        prefix: t('SALES_PARTIES.DOCUMENTS.COMPANY_REQUIRED_PREFIX'),
        highlight: t('SALES_PARTIES.DOCUMENTS.COMPANY_REQUIRED_HIGHLIGHT'),
        suffix: t('SALES_PARTIES.DOCUMENTS.COMPANY_REQUIRED_SUFFIX', {
          action: partyAction.value,
        }),
      }
    : {
        prefix: t('SALES_PARTIES.DOCUMENTS.PERSON_REQUIRED_PREFIX'),
        highlight: t('SALES_PARTIES.DOCUMENTS.PERSON_REQUIRED_HIGHLIGHT'),
        suffix: t('SALES_PARTIES.DOCUMENTS.PERSON_REQUIRED_SUFFIX', {
          action: partyAction.value,
        }),
      };
});
const hasRgDocument = computed(() =>
  documents.documents.value.some(document =>
    document.document_types?.some(type => String(type.id) === '20')
  )
);
const showCpfNotice = computed(
  () => !props.deliveryCorrection && notices.value.cpf && hasRgDocument.value
);
const dismiss = notice => {
  notices.value = { ...notices.value, [notice]: false };
};

const load = async () => {
  await documents.load();
};
const upload = async files => {
  if (await documents.uploadFiles(files)) emit('changed');
};
const remove = async document => {
  if (await documents.remove(document)) emit('changed');
};
const saveTypes = async (document, types) => {
  savingDocumentId.value = document.id;
  const updated = await documents.updateTypes(document, types);
  savingDocumentId.value = null;
  if (updated) emit('changed');
};

onMounted(load);
</script>

<template>
  <section
    data-test="sales-party-documents-section"
    class="rounded-[10px] bg-white p-8 font-roboto shadow-sm max-[700px]:p-0"
  >
    <header class="mb-6 max-[700px]:mb-11">
      <h2
        data-test="sales-party-documents-title"
        class="font-lato text-[32px] font-bold leading-[39px] text-[#464F53] max-[700px]:text-[24px] max-[700px]:leading-[29px]"
      >
        {{ title }}
      </h2>
      <p
        class="mt-2 w-[52vw] max-w-full text-xl font-normal leading-6 text-[#464F53] max-[700px]:w-full"
      >
        {{ description }}
      </p>
    </header>
    <div class="grid gap-[5px]">
      <SalesPartyDocumentsUploader
        :documents="documents.documents.value"
        :pending="documents.pending.value"
        :loading="documents.loading.value"
        :can-upload="canEdit"
        :document-types="documentTypes"
        :saving-document-id="savingDocumentId"
        :show-empty-state="false"
        @files="upload"
        @download="documents.download"
        @remove="remove"
        @save-types="saveTypes"
      />
      <SalesPartyDocumentNotice
        v-if="!deliveryCorrection && notices.required"
        data-test="document-notice-required"
        tone="danger"
        @dismiss="dismiss('required')"
      >
        {{ requiredNoticeCopy.prefix }}
        <strong>{{ requiredNoticeCopy.highlight }}</strong>
        {{ requiredNoticeCopy.suffix }}
      </SalesPartyDocumentNotice>
      <SalesPartyDocumentNotice
        v-if="!deliveryCorrection && notices.legible"
        data-test="document-notice-legible"
        @dismiss="dismiss('legible')"
      >
        {{ t('SALES_PARTIES.DOCUMENTS.LEGIBLE_PREFIX') }}
        <strong>{{ t('SALES_PARTIES.DOCUMENTS.LEGIBLE_HIGHLIGHT') }}</strong>
        {{ t('SALES_PARTIES.DOCUMENTS.LEGIBLE_SUFFIX') }}
      </SalesPartyDocumentNotice>
      <SalesPartyDocumentNotice
        v-if="showCpfNotice"
        data-test="document-notice-cpf"
        @dismiss="dismiss('cpf')"
      >
        {{ t('SALES_PARTIES.DOCUMENTS.CPF_WARNING') }}
      </SalesPartyDocumentNotice>
    </div>
  </section>
</template>
